import type { SwiperOptions } from 'swiper/types'
import injectedStyles from './swiper-injected-styles.raw.css'
import BREAKPOINTS from '../../../assets/constants/breakpoints'
import { SPACE_BETWEEN_SLIDES } from '../../../assets/constants/slider'

const DEFAULT_PROPS = {
  direction: 'horizontal',
  slidesPerView: 1,
  spaceBetween: SPACE_BETWEEN_SLIDES.DESKTOP,
  roundLengths: true,
  effect: 'slide',
  creativeEffect: undefined,
  breakpoints: {},
  injectStyles: [injectedStyles],
  grabCursor: true,
  pagination: {
    enabled: true,
    clickable: true,
    type: 'bullets'
  },
  mousewheel: {
    enabled: true,
    forceToAxis: true
  },
  navigation: {
    enabled: true
  }
} as const satisfies SwiperOptions

type TemplateOptions = Pick<SwiperOptions, keyof typeof DEFAULT_PROPS>

const makeCreativeEffectProps = (spaceBetween: number): TemplateOptions => ({
  injectStyles: [
    `
    .swiper {
      overflow: visible;
    }
    `
  ],
  effect: 'creative',
  creativeEffect: {
    limitProgress: Number.POSITIVE_INFINITY,
    prev: {
      shadow: true,
      translate: [0, 0, -400],
      opacity: -1 // With 0 the slide will animate to 0.03 opacity instead of 0
    },
    next: {
      translate: [`calc(100% + ${spaceBetween}px)`, 0, 0]
    }
  }
})

const creativeEffectPropsMobile = makeCreativeEffectProps(
  SPACE_BETWEEN_SLIDES.MOBILE
)
const creativeEffectProps = makeCreativeEffectProps(SPACE_BETWEEN_SLIDES.DESKTOP)

function mergeProps(
  props: TemplateOptions,
  overrides: TemplateOptions | undefined
): TemplateOptions {
  const result = {
    ...props,
    ...overrides
  }
  result.injectStyles = [
    ...(props.injectStyles ?? []),
    ...(overrides?.injectStyles ?? [])
  ]
  return result
}

const swiperTemplates = [
  {
    name: 'default',
    label: 'Standard (Volle Breite)',
    props: {
      slidesPerView: 1,
      injectStyles: [
        `
        .swiper {
          overflow: visible;
        }
        `
      ]
    }
  },
  {
    name: '3-col',
    label: '3 Spalten',
    props: mergeProps(
      {
        slidesPerView: 1.06,
        spaceBetween: SPACE_BETWEEN_SLIDES.MOBILE,
        breakpoints: {
          [BREAKPOINTS.sm]: {
            ...creativeEffectProps
          },
          [BREAKPOINTS.md]: {
            slidesPerView: 2,
            spaceBetween: SPACE_BETWEEN_SLIDES.DESKTOP,
            ...creativeEffectProps
          },
          [BREAKPOINTS.lg]: {
            slidesPerView: 3,
            spaceBetween: SPACE_BETWEEN_SLIDES.DESKTOP,
            ...creativeEffectProps
          }
        }
      },
      creativeEffectPropsMobile
    )
  },
  {
    name: '4-col',
    label: '4 Spalten',
    props: mergeProps(
      {
        slidesPerView: 1.06,
        spaceBetween: SPACE_BETWEEN_SLIDES.MOBILE,
        breakpoints: {
          [BREAKPOINTS.sm]: {
            slidesPerView: 2,
            ...creativeEffectProps
          },
          [BREAKPOINTS.md]: {
            slidesPerView: 3,
            spaceBetween: SPACE_BETWEEN_SLIDES.DESKTOP,
            ...creativeEffectProps
          },
          [BREAKPOINTS.xl]: {
            slidesPerView: 4,
            spaceBetween: SPACE_BETWEEN_SLIDES.DESKTOP,
            ...creativeEffectProps
          }
        }
      },
      creativeEffectPropsMobile
    )
  }
] as const satisfies Array<{
  name: string
  label: string
  // Only allow to override the props that are defined in DEFAULT_PROPS
  // Such that when we switch the template, we know that we reset any props that have been set by a different template
  props: TemplateOptions
}>

export function getTemplateProps(
  name: string | undefined | null
): SwiperOptions {
  const props = swiperTemplates.find(
    (template) => template.name === name
  )?.props
  return mergeProps(DEFAULT_PROPS, props)
}

export default swiperTemplates
